import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, CircularProgress, FormControlLabel, MenuItem, TextField, Typography } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import InputAdornment from "@material-ui/core/InputAdornment";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNotificationContext } from "@providers/NotificationProvider";
import { calendarToday } from "utils/date";
import BookingCard from "./BookingCard";
import DashboardDialogs from "./dialogs";
import {
  filterRecords, formatWhen, latestTask, leadName, messageTaskId, recordMatches, selectedItemIds,
  sortHint, stageDefinitions, storedTaskMessage, sumUnread, taskCategories, toggleBoatGroup, unreadSummary, SORTS,
} from "./model";
import {
  Change, Columns, CompareButton, CompareList, Empty, Filters, Metric, MetricLabel, MetricLine, MetricLines,
  MetricNumber, MetricPeriod, MetricPrimary, Metrics, Page, Pill, Pills, PrimaryButton, QueueBadge, QueueButton,
  QueueCount, QueuePair, Queues, Results, SearchField, SearchHit, SecondaryButton, SortField, SortHint, StatsToggle,
  StatusRow, TextButton, Tools, Workflow, WorkflowTitle, WorkflowTotal,
} from "./styles";

const STATS_KEY = "ucruise-booking-dashboard:stats-hidden";
const INITIAL_ROWS = 10;
const ROW_STEP = 10;
const NO_ENTRIES = new Map();

function readStatsHidden() {
  try {
    return { hidden: window.sessionStorage.getItem(STATS_KEY) === "true", error: false };
  } catch (error) {
    return { hidden: false, error: true };
  }
}

function Delta({ value }) {
  if (!Number.isFinite(value)) return null;
  const tone = value < 0 ? "down" : value > 0 ? "up" : "flat";
  const arrow = value < 0 ? "↘" : value > 0 ? "↗" : "→";
  return <Change className={tone}>{arrow} {value.toFixed(1)}% YoY</Change>;
}

function MetricValue({ value }) {
  return value ?? "Unavailable";
}

export default function BookingDashboard({
  payload, error, retrying, refreshEnabled, onRefreshEnabled, onRetry, onAction, onNavigate, onCreateQuote, onCreateOptions, onLoadConversation,
}) {
  const { showNotification } = useNotificationContext();
  const preference = useRef(readStatsHidden());
  const notifiedPreference = useRef(false);
  const selectedBoats = useRef(new Map());
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [statsHidden, setStatsHidden] = useState(preference.current.hidden);
  const [stage, setStage] = useState("");
  const [task, setTask] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInStage, setSearchInStage] = useState(true);
  const [sort, setSort] = useState("newest");
  const [expanded, setExpanded] = useState(() => new Set());
  const [comparisonsOpen, setComparisonsOpen] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_ROWS);

  const stages = useMemo(() => stageDefinitions(payload), [payload]);
  const bookings = payload?.bookings || [];
  const tasksByBooking = payload?.tasksByBooking || NO_ENTRIES;
  const stagesByBooking = payload?.stagesByBooking || NO_ENTRIES;
  const leadTasks = payload?.leadTasks || [];
  const unreadByBooking = useMemo(() => {
    const counts = new Map();
    bookings.forEach((booking) => counts.set(booking.id, unreadSummary(booking)));
    return counts;
  }, [bookings]);
  const categories = useMemo(
    () => (payload ? taskCategories(bookings, tasksByBooking, stagesByBooking, leadTasks) : []),
    [payload, bookings, tasksByBooking, stagesByBooking, leadTasks],
  );

  if (payload && task === null) {
    setTask(categories.find((category) => category.type !== "newEnquiryFollowup")?.type ?? "");
  } else if (task && !categories.some((category) => category.type === task)) {
    setTask("");
  }

  useEffect(() => {
    if (payload && preference.current.error && !notifiedPreference.current) {
      notifiedPreference.current = true;
      showNotification("Your stats preference could not be restored.");
    }
  }, [payload, showNotification]);

  const activeTask = task || "";
  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () => filterRecords(bookings, { stage, task: activeTask, query, searchInStage, sort, stagesByBooking, tasksByBooking, unreadByBooking }),
    [bookings, stage, activeTask, query, searchInStage, sort, stagesByBooking, tasksByBooking, unreadByBooking],
  );
  const filterKey = `${stage}:${activeTask}:${query}:${searchInStage}:${sort}`;
  const [visibleFilter, setVisibleFilter] = useState(filterKey);
  if (visibleFilter !== filterKey) {
    setVisibleFilter(filterKey);
    setVisibleCount(INITIAL_ROWS);
  }
  const visibleRows = rows.slice(0, visibleFilter === filterKey ? visibleCount : INITIAL_ROWS);
  const hint = sortHint(bookings, sort, unreadByBooking);
  const today = calendarToday();
  const selectedByBooking = selectionVersion >= 0 ? selectedBoats.current : selectedBoats.current;
  const historyAvailable = payload?.capabilities?.bookingHistory === true;

  const notify = (message) => showNotification(message);

  const go = (message) => {
    try {
      onNavigate(message);
    } catch (navigationError) {
      notify(navigationError.message);
    }
  };

  const copyReference = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      notify("Copied to clipboard");
    } catch (copyError) {
      notify("Copy was unavailable. Select the reference and copy it manually.");
    }
  };

  const openAction = (type, booking) => {
    if (type === "note") {
      setDialog({ type: "note", record: booking });
      return;
    }
    if (type === "message") {
      const current = latestTask(booking, tasksByBooking, stagesByBooking);
      setDialog({ type: "message", record: booking, taskId: messageTaskId(current), initialBody: storedTaskMessage(current) });
      return;
    }
    if (type === "enquiry") {
      setDialog({ type: "conversation", record: booking });
      return;
    }
    if (type === "new-quote") {
      onCreateQuote(booking, selectedItemIds(booking, selectedBoats.current));
      return;
    }
    if (type === "new-options") {
      const ids = selectedItemIds(booking, selectedBoats.current);
      if (!ids.length) {
        notify("Select at least one boat first.");
        return;
      }
      onCreateOptions(booking, ids);
      return;
    }
    const navigation = {
      edit: "edit-booking", options: "options", quotes: "quotes", merge: "merge", delete: "delete-booking",
      "add-boat": "add-boat", booking: "booking", lead: "lead", crew: "crew", history: "history",
      conversation: "conversation", phone: "phone", spam: "enquiries",
    };
    if (navigation[type]) {
      go({ action: navigation[type], bookingId: booking.id, leadId: booking.lead?.id });
      return;
    }
    if (type === "availability") { setDialog({ type: "availability", record: booking }); return; }
    if (type === "availability-request") { setDialog({ type: "availability-request", record: booking }); return; }
    if (type === "task-done") {
      const current = latestTask(booking, tasksByBooking, stagesByBooking);
      if (current) setDialog({ type: "task-done", record: booking, task: current });
      return;
    }
    if (type === "booking-availability") { setDialog({ type: "booking-availability", record: booking }); return; }
    if (type === "reference") copyReference(booking.reference);
  };

  const clearFilters = () => {
    setStage("");
    setTask("");
    setSearch("");
  };

  const toggleStats = () => {
    const next = !statsHidden;
    setStatsHidden(next);
    try {
      window.sessionStorage.setItem(STATS_KEY, String(next));
    } catch (storageError) {
      notify("Your choice could not be remembered for this session.");
    }
  };

  const metricsTitle = payload?.statsUpdatedAt
    ? `Updated ${formatWhen(payload.statsUpdatedAt, true)}`
    : "Update time unavailable";

  const now = Date.now();
  const onSent = (booking, result) => {
    const completion = result.taskCompletion;
    const partial = completion && ["changed", "failed"].includes(completion.status);
    if (partial || result.refreshRequired) {
      setDialog({
        type: "sent",
        name: leadName(booking.lead) || booking.reference,
        message: partial ? completion.message : "The latest booking details could not be loaded. Refresh the page before sending another message.",
      });
      return;
    }
    notify(completion && ["completed", "already-completed"].includes(completion.status) ? "Message sent. Task completed." : "Message sent");
    setDialog(null);
  };

  return (
    <Page>
      {!payload && !error ? (
        <StatusRow>
          <CircularProgress size={24} />
          <Typography>Loading bookings...</Typography>
        </StatusRow>
      ) : null}
      {error ? (
        <StatusRow role="alert">
          <Typography>{error}</Typography>
          <SecondaryButton onClick={onRetry} disabled={retrying}>{retrying ? "Trying again..." : "Try again"}</SecondaryButton>
        </StatusRow>
      ) : null}
      {payload ? (
        <>
          <StatsToggle>
            <TextButton onClick={toggleStats} aria-expanded={!statsHidden} aria-controls="booking-metrics">
              {statsHidden ? "Show stats" : "Hide stats"}
            </TextButton>
          </StatsToggle>
          {statsHidden ? null : (
            <Metrics id="booking-metrics" title={metricsTitle}>
              {payload.metrics.map((metric) => (
                <Metric key={metric.title} className={metric.mtdTone === "up" ? "up" : metric.mtdTone === "down" ? "down" : ""}>
                  <MetricLabel>{metric.title}</MetricLabel>
                  <MetricPrimary>
                    <div>
                      <MetricNumber><MetricValue value={metric.main} /></MetricNumber>
                      <MetricPeriod>Last 30 days</MetricPeriod>
                    </div>
                    <Delta value={metric.change} />
                  </MetricPrimary>
                  <MetricLines>
                    <MetricLine><span>Last 24 hours</span><span><MetricValue value={metric.last24} /></span><Delta value={metric.last24Change} /></MetricLine>
                    <MetricLine><span>Last 7 days</span><span><MetricValue value={metric.seven} /></span><Delta value={metric.sevenChange} /></MetricLine>
                    <MetricLine><span>Month to date</span><span><MetricValue value={metric.mtd} /></span><Delta value={metric.mtdChange} /></MetricLine>
                    <MetricLine className="last"><span>This month last year</span><span><MetricValue value={metric.last} /></span></MetricLine>
                  </MetricLines>
                  <CompareButton type="button" aria-expanded={comparisonsOpen} onClick={() => setComparisonsOpen((open) => !open)}>
                    Prior-year comparisons
                  </CompareButton>
                  {comparisonsOpen ? (
                    <CompareList>
                      <div><dt>Last 7 days</dt><dd><MetricValue value={metric.priorSeven} /></dd></div>
                      <div><dt>Last 30 days</dt><dd><MetricValue value={metric.priorMain} /></dd></div>
                      <div><dt>Month to date</dt><dd><MetricValue value={metric.priorMtd} /></dd></div>
                    </CompareList>
                  ) : null}
                </Metric>
              ))}
            </Metrics>
          )}
          <Workflow aria-label="Tasks">
            <WorkflowTitle>Work queue <WorkflowTotal>{categories.reduce((total, category) => total + category.count, 0)}</WorkflowTotal></WorkflowTitle>
            <Pills>
              {categories.map((category) => (
                <Pill
                  key={category.type}
                  type="button"
                  className={activeTask === category.type ? "active" : ""}
                  aria-pressed={activeTask === category.type}
                  onClick={() => {
                    if (category.type === "newEnquiryFollowup" && leadTasks.length) {
                      setDialog({ type: "enquiries", tasks: leadTasks, offset: 0 });
                      return;
                    }
                    setTask(activeTask === category.type ? "" : category.type);
                    setStage("");
                  }}
                >
                  {category.label}<strong>{category.count}</strong>
                </Pill>
              ))}
            </Pills>
            <PrimaryButton onClick={() => go({ action: "new-booking" })}>+ New booking</PrimaryButton>
          </Workflow>
          <Queues>
            {stages.reduce((pairs, item, index) => {
              if (index % 2 === 0) pairs.push(stages.slice(index, index + 2));
              return pairs;
            }, []).map((pair) => (
              <QueuePair key={pair[0].key}>
                {pair.map((item) => {
                  const matching = bookings.filter((booking) => stagesByBooking.get(booking.id)?.includes(item.key));
                  const unread = sumUnread(matching, unreadByBooking);
                  const hit = query && matching.some((booking) => recordMatches(booking, query));
                  const active = stage === item.key;
                  return (
                    <QueueButton
                      key={item.key}
                      type="button"
                      className={active ? "active" : ""}
                      title={item.tooltip}
                      aria-pressed={active}
                      onClick={() => {
                        setStage(active ? "" : item.key);
                        setTask("");
                        setSearchInStage(true);
                      }}
                    >
                      <span>{item.label}</span>
                      <QueueCount>{matching.length}</QueueCount>
                      {unread > 0 ? (
                        <QueueBadge aria-label={`${unread} unread messages`}>
                          {unread > 99 ? "99+" : unread}
                        </QueueBadge>
                      ) : null}
                      {hit ? <SearchHit title="Contains a search match">!</SearchHit> : null}
                    </QueueButton>
                  );
                })}
              </QueuePair>
            ))}
          </Queues>
          <Tools>
            <Results>
              <span>{rows.length} booking{rows.length === 1 ? "" : "s"}</span>
              {stage || activeTask || query ? <TextButton onClick={clearFilters}>Clear filters</TextButton> : null}
            </Results>
            <Filters>
              <FormControlLabel
                control={<Checkbox color="primary" checked={searchInStage} onChange={(event) => setSearchInStage(event.target.checked)} />}
                label="Search in status"
              />
              <FormControlLabel
                control={<Checkbox color="primary" checked={refreshEnabled} onChange={(event) => onRefreshEnabled(event.target.checked)} />}
                label="Auto refresh"
              />
              <SearchField>
                <TextField
                  value={search}
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Reference, lead, boat or cruise date"
                  inputProps={{ "aria-label": "Search bookings" }}
                  onChange={(event) => setSearch(event.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  }}
                />
              </SearchField>
              <SortField>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Sort by"
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  inputProps={{ "aria-label": "Sort bookings" }}
                >
                  {SORTS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
              </SortField>
            </Filters>
          </Tools>
          <SortHint role="status">{hint}</SortHint>
          <Columns>
            <div>Customer & Booking Details</div>
            <div>Latest Task</div>
            <div>Notes & Actions</div>
          </Columns>
          {rows.length ? (
            <InfiniteScroll
              key={filterKey}
              style={{ width: "100%" }}
              height="calc(100vh - 250px)"
              dataLength={visibleRows.length}
              next={() => setVisibleCount((count) => count + ROW_STEP)}
              hasMore={visibleRows.length < rows.length}
              loader={<h4>Loading more 10 rows...</h4>}
            >
              {visibleRows.map((booking) => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  tasksByBooking={tasksByBooking}
                  stagesByBooking={stagesByBooking}
                  unreadByBooking={unreadByBooking}
                  stages={stages}
                  today={today}
                  now={now}
                  expanded={expanded.has(booking.id)}
                  selectedByBooking={selectedByBooking}
                  historyAvailable={historyAvailable}
                  onAction={openAction}
                  onToggle={(id) => setExpanded((current) => {
                    const next = new Set(current);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return next;
                  })}
                  onSelectBoat={(bookingToUpdate, itemId, checked) => {
                    if (toggleBoatGroup(bookingToUpdate, itemId, checked, selectedBoats.current)) {
                      setSelectionVersion((version) => version + 1);
                    }
                  }}
                />
              ))}
            </InfiniteScroll>
          ) : (
            <Empty>
              <strong>No bookings found</strong>
              <SecondaryButton onClick={clearFilters}>Clear filters</SecondaryButton>
            </Empty>
          )}
        </>
      ) : null}
      <DashboardDialogs
        dialog={dialog}
        onAction={onAction}
        onLoadConversation={onLoadConversation}
        onNotify={notify}
        onNavigate={onNavigate}
        onChange={setDialog}
        onClose={() => setDialog(null)}
        onSent={onSent}
      />
    </Page>
  );
}
